import { useState } from 'react'
import {
  ACTIVITY_AGE_LABEL,
  agentLabel,
  AUTHORITY_LABEL,
  type RoomState,
} from '@cs/shared'
import { api } from '../api.js'
import { Links } from './Links.js'
import { describeError } from '../App.js'

const STATUS_LABEL = { working: '作業中', blocked: 'ブロック中', done: '完了' } as const

function ago(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3600_000
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}分前`
  if (hours < 24) return `${Math.round(hours)}時間前`
  return `${Math.round(hours / 24)}日前`
}

/**
 * The PM view. One screen that answers 「今の仕様・実装どうなってますか？」 without
 * anyone having to re-explain: each side's latest approved summary, what the specs
 * say now, what is still open, and what has been decided.
 */
export function Board({
  state,
  room,
  meId,
  canEdit,
  onRefresh,
}: {
  state: RoomState | null
  room: string
  meId: string
  /** Viewers read the board; everyone else keeps the environment list current. */
  canEdit: boolean
  onRefresh: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [requesting, setRequesting] = useState<string | null>(null)

  /**
   * Ask someone for a status update.
   *
   * A button rather than a free-text message, because the thing being asked for is
   * always the same and the wording is the part people put off. It goes to the
   * person, not their Claude: deciding what to disclose about your own work is not
   * something to delegate.
   */
  const requestShare = async (m: { user_id: string; display_name: string }) => {
    // Guarded because the first version was not: a second click while the first
    // request was still in flight sent the request twice, and the recipient got two
    // identical questions with no way to tell them apart.
    if (requesting) return
    setRequesting(m.user_id)
    setError(null)
    try {
      const result = await api.post(room, {
        body:
          `${m.display_name}さん、現時点のもので構わないので、いまの作業状況を共有していただけますか。`,
        kind: 'question',
        mentions: [m.user_id],
        to_role: 'human',
      })
      setNotice(
        result.status === 'duplicate'
          ? `${m.display_name}さんへの依頼は既に送信済みです。まだ回答待ちなので、重ねて送っていません。`
          : `${m.display_name}さんに共有を依頼しました。`,
      )
      onRefresh()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setRequesting(null)
    }
  }

  /** Take back a summary you published. Same honesty rule as the timeline. */
  const retractReport = async (id: string) => {
    if (!window.confirm('このサマリーを取り消しますか？')) return
    try {
      const result = await api.retractReport(room, id)
      setNotice(
        result.status === 'purged'
          ? 'まだ誰も読んでいなかったため、完全に削除しました。'
          : `本文は削除しましたが、${result.seen_by
              .map((r) => `${r.display_name} (${r.org})`)
              .join(' / ')} は既に読んでいます。`,
      )
      onRefresh()
    } catch (err) {
      setError(describeError(err))
    }
  }
  const [decision, setDecision] = useState('')
  const [rationale, setRationale] = useState('')
  const [busy, setBusy] = useState(false)

  if (!state) return <p className="muted">読み込み中…</p>

  const record = async () => {
    if (!decision.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.decide(room, { decision: decision.trim(), rationale: rationale.trim() })
      setDecision('')
      setRationale('')
      onRefresh()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {error && <div className="error">{error}</div>}
      {notice && (
        <div className="notice">
          {notice}
          <button style={{ marginLeft: 10 }} onClick={() => setNotice(null)}>
            閉じる
          </button>
        </div>
      )}

      <div className="card">
        <div className="row">
          <h2 style={{ margin: 0 }}>{state.room.name}</h2>
          <span className="muted">
            {new Date(state.generated_at).toLocaleString('ja-JP')} 時点
          </span>
          <button style={{ marginLeft: 'auto' }} onClick={onRefresh}>
            更新
          </button>
        </div>
      </div>

      {state.waiting_on.length > 0 && (
        <div className="card">
          <h2>誰の回答待ちか</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            待ち時間の長い順です。質問はその領域の責任者に自動で振られます。
          </p>
          {state.waiting_on.map((w) => (
            <div key={w.user_id ?? w.display_name} style={{ marginBottom: 12 }}>
              <div className="row">
                <strong>{w.display_name}</strong>
                {w.org && <span className="muted">{w.org}</span>}
                <span className={`pill ${w.oldest_age_hours >= 24 ? 'block' : 'warn'}`}>
                  {w.count}件 · 最古 {Math.round(w.oldest_age_hours)}時間
                </span>
                {w.unassigned_domain && (
                  <span className="pill block">
                    {AUTHORITY_LABEL[w.unassigned_domain]}の責任者が未設定
                  </span>
                )}
              </div>
              {w.questions.slice(0, 3).map((q) => (
                <div key={q.message_id} className="muted" style={{ marginLeft: 12 }}>
                  ・{q.body.split('\n')[0]?.slice(0, 70)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {state.authority_gaps.some((g) => g.open_question_count > 0) && (
        <div className="card" style={{ borderColor: 'var(--danger)' }}>
          <h2>回答できる人がいない質問があります</h2>
          {state.authority_gaps
            .filter((g) => g.open_question_count > 0)
            .map((g) => (
              <div key={g.domain}>
                <strong>{AUTHORITY_LABEL[g.domain]}</strong> の責任者が未設定のまま、
                {g.open_question_count} 件の質問が滞留しています。
              </div>
            ))}
          <p className="muted" style={{ marginBottom: 0 }}>
            「メンバー」タブで担当を設定すると、以後その領域の質問は自動で届くようになります。
          </p>
        </div>
      )}

      {state.open_questions.length > 0 && (
        <div className="card">
          <h2>未解決の質問 {state.open_questions.length} 件</h2>
          {state.open_questions.map((q) => (
            <div key={q.message_id} style={{ marginBottom: 10 }}>
              <div className="row">
                <strong>
                  {q.from_agent ? agentLabel(q.from_name, q.from_nickname) : q.from_name}
                </strong>
                <span className="muted">
                  {q.from_org} · {ago(q.created_at)}
                </span>
                {q.domain && <span className="pill decision">{AUTHORITY_LABEL[q.domain]}の判断</span>}
                {q.age_hours >= 24 && <span className="pill block">24時間以上未回答</span>}
              </div>
              <div className="msg-body">{q.body}</div>
            </div>
          ))}
        </div>
      )}

      <Links
        room={room}
        links={state.links}
        canEdit={canEdit}
        onChanged={onRefresh}
      />

      <div className="grid2">
        <div className="card">
          <h2>各メンバーの状況</h2>

          {/*
            Activity carries no content at all — only roughly when someone last
            worked on this project and roughly when they last shared something.
            That is deliberately enough to ask a question and not enough to answer
            one: "作業は進んでいるが共有がない" is a reason to say 「現時点のもので
            構わないので共有してください」, which is a conversation between people.
          */}
          {state.members.map((m) => {
            const a = m.activity
            if (a.worked === 'never' && !a.unshared) return null
            return (
              <div key={m.user_id} className="row" style={{ marginBottom: 6, fontSize: 13 }}>
                <strong>{m.display_name}</strong>
                <span className="muted">{m.org}</span>
                <span className="muted">
                  作業 {ACTIVITY_AGE_LABEL[a.worked]} / 共有 {ACTIVITY_AGE_LABEL[a.shared]}
                </span>
                {a.unshared && <span className="pill warn">未共有の作業あり</span>}
                {a.unshared && m.user_id !== meId && (
                  <button
                    style={{ marginLeft: 'auto' }}
                    disabled={requesting !== null}
                    onClick={() => requestShare(m)}
                  >
                    {requesting === m.user_id ? '送信中…' : '共有を依頼する'}
                  </button>
                )}
              </div>
            )
          })}

          <hr style={{ border: 0, borderTop: '1px solid var(--line, #ddd)', margin: '12px 0' }} />

          {state.reports.length === 0 && (
            <p className="muted">
              まだ誰も状況を共有していません。作業が進んでいる人には「共有を依頼する」で
              依頼できます。
            </p>
          )}
          {state.reports.map((r) => (
            <div key={r.id} style={{ marginBottom: 12 }}>
              <div className="row">
                <strong>{r.author_name}</strong>
                <span className="muted">{r.author_org}</span>
                <span className={`pill ${r.status}`}>{STATUS_LABEL[r.status]}</span>
                <span className="muted">{ago(r.updated_at)}</span>
                {r.author_id === meId && !r.retracted_at && (
                  <button style={{ marginLeft: 'auto' }} onClick={() => retractReport(r.id)}>
                    取り消す
                  </button>
                )}
              </div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{r.title}</div>
              {r.retracted_at ? (
                <div className="msg-body msg-retracted">
                  このサマリーは投稿者によって取り消されました。
                </div>
              ) : (
                <div className="msg-body muted">{r.body}</div>
              )}
            </div>
          ))}
        </div>

        <div className="card">
          <h2>仕様書</h2>
          {state.docs.length === 0 && <p className="muted">まだ仕様書がありません。</p>}
          {state.docs.map((d) => (
            <div key={d.path} style={{ marginBottom: 10 }}>
              <div className="row">
                <code>{d.path}</code>
                {d.proposed_count > 0 && (
                  <span className="pill warn">提案 {d.proposed_count} 件</span>
                )}
              </div>
              <div className="muted">
                {d.summary} — {d.updated_by_name} が {ago(d.updated_at)} に更新
              </div>
            </div>
          ))}

          {state.recent_changes.length > 0 && (
            <>
              <h3 style={{ marginTop: 14 }}>直近の更新</h3>
              {state.recent_changes.slice(0, 5).map((v) => (
                <div key={v.version_id} className="muted">
                  {ago(v.created_at)} · <code>{v.path}</code> · {v.author_name}
                  {v.note ? ` — ${v.note}` : ''}
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className="card">
        <h2>決定事項</h2>
        {state.decisions.length === 0 && <p className="muted">まだ記録された決定はありません。</p>}
        {state.decisions.map((d) => (
          <div key={d.id} style={{ marginBottom: 10 }}>
            <div className="row">
              <strong>{d.decision}</strong>
              <span className="muted">
                {d.decided_by_name} · {ago(d.created_at)}
              </span>
            </div>
            {d.domain && (
              <div className="row">
                <span className="pill decision">{AUTHORITY_LABEL[d.domain]}</span>
                {!d.within_authority && (
                  <span className="pill block">
                    {AUTHORITY_LABEL[d.domain]}の責任者以外による決定
                  </span>
                )}
              </div>
            )}
            {d.rationale && <div className="muted">理由: {d.rationale}</div>}
            {d.question_excerpt && <div className="muted">元の質問: {d.question_excerpt}</div>}
          </div>
        ))}

        <h3 style={{ marginTop: 14 }}>決定を記録する</h3>
        <input
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          placeholder="決めたこと"
        />
        <div style={{ height: 8 }} />
        <input
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="理由 (任意)"
        />
        <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
          <button className="primary" disabled={busy || !decision.trim()} onClick={record}>
            記録する
          </button>
        </div>
      </div>

      <div className="card">
        <h2>メンバー</h2>
        <div className="kv">
          {state.members.map((m) => (
            <Row key={m.user_id} member={m} />
          ))}
        </div>
      </div>
    </>
  )
}

function Row({ member }: { member: RoomState['members'][number] }) {
  return (
    <>
      <span>
        {member.display_name} <span className="muted">({member.role})</span>
      </span>
      <span className="muted">
        {member.org}
        {member.last_seen_at ? ` · 最終接続 ${ago(member.last_seen_at)}` : ' · 未接続'}
      </span>
    </>
  )
}
